<template>
    <!-- Forms Container -->
    <div class="" v-if="!isLoading && safeForms.length && safeFormConfig.length">
        <div v-if="!hasAnySectionAccess" class="card border-0 shadow-sm rounded-lg p-4 text-center mb-3">
            <div class="d-flex flex-column align-items-center">
                <div class="rounded-circle bg-light d-flex align-items-center justify-content-center mb-2"
                    style="width: 44px; height: 44px;">
                    <i class="bi bi-lock fs-4 text-muted"></i>
                </div>
                <div class="fw-semibold text-dark text-xxs">Access Restricted</div>
                <div class="text-muted text-xxs mt-1">
                    Oops, you don't have access to this section. Please contact your administrator to assign access.
                </div>
            </div>
        </div>
        <div v-for="(form, fIdx) in safeFormConfig" :key="'form-' + form._mainId" class="form-wrapper"
            :class="{ prev: currentFormIndex > fIdx, active: currentFormIndex === fIdx, next: currentFormIndex < fIdx }">
            <div class="mt-0 border-0">
                <template v-if="form.sections && form.sections.length">
                    <template v-for="(section, sIdx) in form.sections"
                        :key="'section-' + form._mainId + '-' + section.id">
                        <!-- Only show section if it has access and action_name is not NO_ACCESS -->
                        <div v-if="hasSectionAccess(section)" class="mb-1">
                            <div class="border-0 rounded-lg mb-2 p-3 gap-3 section-bg">
                                <!-- Section header -->
                                <div class="section-header text-x fw-bold d-flex justify-content-between align-items-center border-bottom border-2 pb-3"
                                    v-if="section.title" @click="toggle_section(fIdx, sIdx)">

                                    <!-- Left side -->
                                    <div class="d-flex align-items-center gap-2 flex-shrink-0">
                                        <span class="text-capitalize">{{ section.title }}</span>
                                        <span :class="section.is_verified ? 'text-success' : 'text-danger'"
                                            class="fs-5">
                                            <i
                                                :class="section.is_verified ? 'bi bi-check-circle' : 'bi bi-exclamation-circle'"></i>
                                        </span>
                                    </div>

                                <!-- Right side: fully responsive -->
                                    <div class="section-header-right">
                                        <!-- Badge -->
                                        <span class="status-badge text-xs"
                                            :class="section.is_verified ? 'manually-verified' : 'pending'">
                                            {{ section.is_verified ? 'Manually Verified' : 'Pending Verification' }}
                                        </span>

                                        <!-- Manual verify checkbox (Admin only) -->
                                        <div v-if="userRole === 'System Admin' && hasWriteAccess(section)
                                         && !section.is_verified"
                                            class="form-check mb-0" @click.stop>
                                            <input class="form-check-input shadow-sm" type="checkbox"
                                                v-model="section.is_verified"
                                                :id="`section-${section.id}-manual`">
                                            <label class="form-check-label text-xxs mb-0">Manual Verify</label>
                                        </div>
                                        <!-- Chevron -->
                                        <i :class="is_section_open(fIdx, sIdx) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
                                    </div>
                                </div>

                                <!-- Fields -->
                                <div class="mt-1"
                                    v-if="section.fields && section.fields.length && is_section_open(fIdx, sIdx) && safeForms[fIdx]">
                                    <template v-for="(field, fieldIndex) in section.fields"
                                        :key="'field-' + form._mainId + '-' + section.id + '-' + field.id">

                                        <!-- ── SIMPLE INPUT TYPES (text / textarea / number / date) ── -->
                                        <template v-if="['text','textarea','number','date'].includes(field.type)">

                                            <!-- VIEW MODE -->
                                            <template
                                                v-if="!is_field_editing(form._mainId + '-' + section.id + '-' + field.id)">
                                                <div
                                                    class="field-view-row d-flex justify-content-between align-items-start mt-2 px-2 py-1 rounded">
                                                    <div class="d-flex flex-grow-1 min-w-0">
                                                        <span class="text-xs fw-7 me-1 text-muted flex-shrink-0">{{
                                                            fieldIndex + 1 }}.</span>
                                                        <div class="d-flex flex-column flex-grow-1 min-w-0">
                                                            <span class="text-xs fw-7 text-capitalize text-secondary">{{
                                                                field.label }}</span>
                                                            <small v-if="field.description"
                                                                class="text-muted text-xxs mt-1">{{ field.description
                                                                }}</small>
                                                            <span v-if="safeForms[fIdx][field.model]"
                                                                class="text-xs text-dark mt-1">{{
                                                                safeForms[fIdx][field.model] }}</span>
                                                            <span v-else class="text-xxs text-muted fst-italic mt-1">No
                                                                Answer</span>
                                                        </div>
                                                    </div>
                                                    <!-- Edit button — only shown when user has write access -->
                                                    <button v-if="hasWriteAccess(section)"
                                                        class="btn btn-sm btn-outline-secondary ms-2 px-2 py-1"
                                                        @click.stop="toggle_field_edit(form._mainId + '-' + section.id + '-' + field.id)"
                                                        title="Edit">
                                                        <i class="bi bi-pencil"></i>
                                                    </button>
                                                </div>
                                            </template>

                                            <!-- EDIT MODE — highlighted background -->
                                            <template v-else>
                                                <div class="field-edit-block mt-2 p-2 rounded-lg">
                                                    <div class="d-flex align-items-center gap-1 mb-2">
                                                        <span class="text-xs fw-7 text-muted me-1">{{ fieldIndex + 1
                                                            }}.</span>
                                                        <span class="text-xs fw-7 text-capitalize field-edit-label">{{
                                                            field.label }}</span>
                                                    </div>
                                                    <small v-if="field.description"
                                                        class="text-muted text-xxs ms-1 mb-1 d-block">{{
                                                        field.description }}</small>

                                                    <!-- text -->
                                                    <input v-if="field.type === 'text'" type="text"
                                                        class="form-control form-control-sm text-xxs bg-white border py-1 rounded-lg"
                                                        v-model="safeForms[fIdx][field.model]"
                                                        :placeholder="'No Answer'"
                                                        :disabled="!hasWriteAccess(section)" />

                                                    <!-- textarea -->
                                                    <textarea v-else-if="field.type === 'textarea'"
                                                        class="form-control form-control-sm text-xxs bg-white border py-1 rounded-lg"
                                                        v-model="safeForms[fIdx][field.model]"
                                                        :placeholder="'No Answer'"
                                                        :disabled="!hasWriteAccess(section)"></textarea>

                                                    <!-- number -->
                                                    <input v-else-if="field.type === 'number'" type="text"
                                                        @input="handleNumberInput($event, field, fIdx)"
                                                        class="form-control form-control-sm text-xxs bg-white border py-1 rounded-lg"
                                                        v-model="safeForms[fIdx][field.model]"
                                                        :disabled="!hasWriteAccess(section)" />

                                                    <!-- date -->
                                                    <input v-else-if="field.type === 'date'" type="date"
                                                        class="form-control form-control-sm text-xxs bg-white border py-1 rounded-lg"
                                                        v-model="safeForms[fIdx][field.model]"
                                                        :disabled="!hasWriteAccess(section)" />

                                                    <!-- Cancel / Save -->
                                                    <div
                                                        class="d-flex justify-content-end align-items-center gap-2 mt-2">
                                                        <button class="btn btn-sm btn-light border text-xxs px-3"
                                                            @click.stop="toggle_field_edit(form._mainId + '-' + section.id + '-' + field.id)">
                                                            Cancel
                                                        </button>
                                                        <button class="btn btn-sm btn-primary-dark text-xxs px-3"
                                                            @click.stop="toggle_field_edit(form._mainId + '-' + section.id + '-' + field.id)" @click="submitAnswers">
                                                            Save
                                                        </button>
                                                    </div>
                                                </div>
                                            </template>

                                        </template>
                                        <!-- ── END simple types ── -->

                                        <!-- ── ALL OTHER TYPES — original code unchanged ── -->
                                        <template v-else>

                                        <!-- Main Question Container -->
                                        <div class="d-flex justify-content-between align-items-start mt-1">
                                            <div class="d-flex flex-grow-1">
                                                <span class="text-xs fw-7 me-1">{{fieldIndex + 1 }}.</span>
                                                <label class="flex-grow-1 text-dark text-xs text-capitalize fw-7">{{
                                                    field.label }}</label>
                                                <div v-if="['single_select', 'multi_select', 'radio', 'checkboxes'].includes(field.type)"
                                                    class="mb-1">
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Question Description -->
                                        <div v-if="field.description" class="ms-3 mb-1">
                                            <small class="text-dark text-xxs">{{ field.description }}</small>
                                        </div>

                                            <!-- Main Question Answer -->
                                            <div class="mb-3 ms-2">
                                            <!-- text -->
                                            <input v-if="field.type === 'text'" type="text"
                                                class="form-control form-control-sm text-xxs bg-white border py-1 rounded-lg"
                                                v-model="safeForms[fIdx][field.model]"
                                                :placeholder="'No Answer'"
                                                :disabled="!hasWriteAccess(section)" />

                                            <!-- textarea -->
                                            <textarea v-if="field.type === 'textarea'"
                                                class="form-control form-control-sm text-xxs bg-white border py-1 rounded-lg"
                                                v-model="safeForms[fIdx][field.model]"
                                                :placeholder="'No Answer'"
                                                :disabled="!hasWriteAccess(section)"></textarea>

                                            <!-- number -->
                                           <input v-if="field.type === 'number'" type="text" @input="handleNumberInput($event, field, fIdx)"
                                                class="form-control form-control-sm text-xxs bg-white border py-1 rounded-lg"
                                                v-model="safeForms[fIdx][field.model]"
                                                :disabled="!hasWriteAccess(section)" />

                                            <!-- date -->
                                            <input v-if="field.type === 'date'" type="date"
                                                class="form-control form-control-sm text-xxs bg-white border py-1 rounded-lg"
                                                v-model="safeForms[fIdx][field.model]"
                                                :disabled="!hasWriteAccess(section)" />

                                                <!-- single select -->
                                                <div v-if="field.type === 'single_select' || field.type === 'radio'"
                                                    class="radio-group">
                                                    <div v-for="(option, optIndex) in field.options || []"
                                                        :key="`radio-${field.model}-${optIndex}`" class="form-check">
                                                        <input class="form-check-input" type="radio"
                                                            :name="`r-${field.model}`" :id="`${field.id}-${optIndex}`"
                                                            v-model="safeForms[fIdx][field.model]"
                                                            :value="String(option.value)"
                                                            @change="update_dependent_questions_visibility(field, safeForms[fIdx][field.model])"
                                                            :disabled="!hasWriteAccess(section)" />
                                                        <label class="form-check-label text-xxs"
                                                            :for="`${field.id}-${optIndex}`">
                                                            {{ option.text }}
                                                        </label>
                                                    </div>
                                                    <div v-if="!field.options?.length" class="text-muted text-xxs">No
                                                        options</div>
                                                </div>

                                                <!-- multi select -->
                                                <div
                                                    v-if="field.type === 'multi_select' || field.type === 'checkboxes'">
                                                    <div v-for="(option, optIndex) in field.options || []"
                                                        :key="`chk-${field.model}-${optIndex}`" class="form-check">
                                                        <input class="form-check-input" type="checkbox"
                                                            :id="`${field.id}-${optIndex}`"
                                                            :value="String(option.value)"
                                                            v-model="safeForms[fIdx][field.model]"
                                                            :disabled="!hasWriteAccess(section)" />
                                                        <label class="form-check-label text-xxs"
                                                            :for="`${field.id}-${optIndex}`">
                                                            {{ option.text }}
                                                        </label>
                                                    </div>
                                                </div>

                                            <!-- file -->
                                            <div v-else-if="field.type === 'file'">
                                                <!-- FILE EXISTS -->
                                                <template v-if="file_exitsts(fIdx, field)">
                                                    <div class="d-flex align-items-center justify-content-between border rounded-lg px-3 py-2 bg-white shadow-sm">
                                                        <div class="d-flex align-items-center gap-2 min-w-0">
                                                            <i class="bi bi-file-earmark text-muted flex-shrink-0"></i>
                                                            <div class="d-flex flex-column min-w-0">
                                                                <span class="text-xs fw-semibold text-truncate">{{ file_name(fIdx, field) }}</span>
                                                            </div>
                                                        </div>
                                                        <div class="d-flex gap-3 flex-shrink-0 ms-2">
                                                            <a href="#" class="text-muted text-decoration-none"
                                                                @click.prevent="open_file_preview(fIdx, field)" title="Preview">
                                                                <i class="bi bi-eye"></i>
                                                            </a>
                                                            <a v-if="hasWriteAccess(section)" href="#" class=" text-muted text-decoration-none"
                                                                @click.prevent="remove_file(fIdx, field.model)" title="Remove">
                                                                <i class="bi bi-trash"></i>
                                                            </a>
                                                        </div>
                                                    </div>
                                                </template>

                                                <!-- FILE MISSING -->
                                                <template v-else>
                                                    <div class="field-view-row d-flex justify-content-between align-items-start mt-2 px-2 py-1 rounded">
                                                        <div class="d-flex flex-grow-1 min-w-0">
                                                            <div v-if="hasWriteAccess(section)" class="flex-grow-1">
                                                                <input type="file"
                                                                    class="form-control form-control-sm text-xxs bg-light border py-1 rounded-lg h-auto"
                                                                    :id="field.id || ''"
                                                                    @change="handle_file_upload($event, field, fIdx)" />
                                                            </div>
                                                            <div v-else class="mt-1">
                                                                <span class="text-xxs text-muted fst-italic">No file uploaded</span>
                                                            </div>
                                                        </div>
                                                        <!-- Chat button in same position as pencil button on other fields -->
                                                        <button class="btn btn-sm btn-outline-secondary ms-2 px-2 py-1"
                                                            @click.prevent="open_comment_modal(field)"
                                                            data-bs-toggle="modal" data-bs-target="#comment_modal" title="Comments">
                                                            <i class="bi bi-chat-left-text"></i>
                                                        </button>
                                                    </div>
                                                </template>
                                            </div>

                                            <!-- facial verification -->
                                            <div v-else-if="field.type === 'facial_verification'">
                                                <template v-if="file_exitsts(fIdx, field)">
                                                    <div class="d-flex align-items-center justify-content-between flex-wrap">
                                                        <!-- Input box showing file name -->
                                                        <input
                                                            type="text"
                                                            class="form-control form-control-sm text-xxs bg-light border py-1 rounded-lg verification-card"
                                                            :value="file_name(fIdx, field)"
                                                            readonly
                                                        />
                                                        <!-- Preview icon aligned to the right -->
                                                        <div class="d-flex gap-3">
                                                            <a
                                                                href="#"
                                                                class="text-dark text-decoration-none"
                                                                @click.prevent="open_file_preview(fIdx, field)"
                                                                title="Preview">
                                                                <i class="bi bi-eye"></i>
                                                            </a>
                                                        </div>
                                                    </div>
                                                </template>

                                                <div v-else-if="hasWriteAccess(section)"
                                                    class="form-control form-control-sm text-xxs bg-white border py-1 rounded-lg">
                                                    Facial verification component would appear here
                                                </div>
                                                <div v-else class="form-control form-control-sm text-xxs bg-white border py-1 rounded-lg">
                                                    No file uploaded - Upload disabled for read-only
                                                    access
                                                </div>
                                            </div>

                                            <!-- fallback -->
                                            <input
                                                v-if="['text','textarea','number','date','single_select','multi_select','checkboxes','file','facial_verification'].indexOf(field.type) === -1"
                                                type="text"
                                                class="form-control form-control-sm text-xxs bg-white border py-1 rounded-lg"
                                                :id="field.id || ''"
                                                v-model="safeForms[fIdx][field.model]"
                                                :placeholder="field.placeholder || 'No Answer'"
                                                :required="field.required"
                                                :disabled="!hasWriteAccess(section)" />
                                        </div>

                                        <!-- Dependent Questions Section -->
                                        <div
                                        v-if="field.dependent_questions && field.dependent_questions.length"
                                        class="mt-2 ms-4 ps-3 border-start border-3 border-primary"
                                        >
                                        <!-- Group label -->
                                        <div
                                            v-for="(dq, dq_index) in field.dependent_questions"
                                            :key="'dq-' + dq_index"
                                            v-show="dq.is_visible"
                                            class=" rounded mb-3"
                                        >


                                            <!-- Dependent question label -->
                                            <div class="d-flex align-items-start mb-1">
                                            <span class="text-xs fw-7 me-1">
                                                {{ fieldIndex + 1 }}.{{ dq_index + 1 }}
                                            </span>
                                            <label class="text-dark fw-7 text-xs text-capitalize">
                                                {{ dq.label }}
                                                <span v-if="dq.required" class="text-danger">*</span>
                                            </label>
                                            </div>

                                            <!-- Dependent Question Answer -->
                                            <div class="mt-1">
                                            <input
                                                v-if="dq.type === 'text'"
                                                type="text"
                                                class="form-control form-control-sm text-xxs bg-white border-1 py-1 rounded-lg"
                                                :id="dq.model"
                                                v-model="safeForms[fIdx][dq.model]"
                                                :placeholder="dq.label"
                                                :required="dq.required && dq.is_visible"
                                                :disabled="!hasWriteAccess(section)"
                                            />

                                            <textarea
                                                v-else-if="dq.type === 'textarea'"
                                                class="form-control form-control-sm text-xxs bg-white border-1 py-1 rounded-lg"
                                                :id="dq.model"
                                                v-model="safeForms[fIdx][dq.model]"
                                                :placeholder="dq.label"
                                                rows="3"
                                                :required="dq.required && dq.is_visible"
                                                :disabled="!hasWriteAccess(section)"
                                            ></textarea>

                                            <input
                                                v-else-if="dq.type === 'number'"
                                                type="text" inputmode="numeric" pattern="[0-9]*"
                                                class="form-control form-control-sm text-xxs bg-white border-1 py-1 rounded-lg"
                                                :id="dq.model"
                                                v-model="safeForms[fIdx][dq.model]"
                                                :required="dq.required && dq.is_visible"
                                                :disabled="!hasWriteAccess(section)"
                                            />

                                            <input
                                                v-else-if="dq.type === 'date'"
                                                type="date"
                                                class="form-control form-control-sm text-xxs bg-white border-1 py-1 rounded-lg"
                                                :id="dq.model"
                                                v-model="safeForms[fIdx][dq.model]"
                                                :required="dq.required && dq.is_visible"
                                                :disabled="!hasWriteAccess(section)"
                                            />

                                            <div v-else-if="dq.type === 'facial_verification'"
                                                class="form-control form-control-sm text-xxs bg-white border-1 py-1 rounded-lg">
                                                <div v-if="hasWriteAccess(section)">
                                                    <!-- FacialBiometric component would go here -->
                                                    <div class="text-muted text-xxs">Facial
                                                        verification interface</div>
                                                </div>
                                                <div v-else class="text-muted text-xxs">
                                                    Facial verification disabled for read-only
                                                    access
                                                </div>
                                            </div>

                                            <div v-else-if="dq.type === 'file'">
                                                <div
                                                    v-if="safeForms[fIdx][dq.model] && safeForms[fIdx][dq.model].file_name">
                                                    <div class="d-flex align-items-center justify-content-between flex-wrap">
                                                        <!-- Input box showing file name -->
                                                        <input
                                                            type="text"
                                                            class="form-control form-control-sm text-xxs bg-light border py-1 rounded-lg verification-card"
                                                            :value="file_name(fIdx, dq)"
                                                            readonly
                                                        />
                                                        <!-- Icons aligned to the right -->
                                                        <div class="d-flex gap-3">
                                                            <a
                                                                href="#"
                                                                class="text-dark text-decoration-none"
                                                                @click.prevent="open_file_preview(fIdx, dq)"
                                                                title="Preview">
                                                                <i class="bi bi-eye"></i>
                                                            </a>
                                                            <a
                                                                v-if="hasWriteAccess(section)"
                                                                href="#"
                                                                class="text-danger text-decoration-none"
                                                                @click.prevent="remove_file(fIdx, dq.model)"
                                                                title="Remove">
                                                                <i class="bi bi-x-lg"></i>
                                                            </a>
                                                            <a
                                                                class="text-dark text-decoration-none"
                                                                @click.prevent="open_comment_modal(dq)"
                                                                data-bs-toggle="modal"
                                                                data-bs-target="#comment_modal"
                                                                title="Comments"
                                                            >
                                                                <i class="bi bi-chat-left-text-fill"></i>
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div v-else>
                                                    <input v-if="hasWriteAccess(section)"
                                                        type="file"
                                                        class="form-control form-control-sm text-xxs bg-white border-1 py-1 rounded-lg"
                                                        :id="dq.model"
                                                        @change="handle_file_upload($event, dq, fIdx)"
                                                        :required="dq.required && dq.is_visible" />

                                                    <div v-if="!safeForms[fIdx][dq.model]?.file"
                                                        class="mt-2">
                                                        <label class="text-xs"
                                                            :for="dq.model + '_file_comment'">
                                                            Please explain why you cannot upload
                                                            this file:
                                                        </label>
                                                        <textarea :id="dq.model + '_file_comment'"
                                                            class="form-control form-control-sm text-xs bg-white border-1 py-1 rounded-lg"
                                                            v-model="safeForms[fIdx][dq.model + '_file_comment']"
                                                            rows="2"
                                                            placeholder="Provide a reason..."
                                                            :disabled="!hasWriteAccess(section)"></textarea>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Radio -->
                                            <div
                                                v-else-if="dq.type === 'single_select' || dq.type === 'radio'"
                                                class="radio-group mt-2"
                                            >
                                                <div
                                                v-for="(option, opt_index) in dq.options || []"
                                                :key="'dq-radio-' + opt_index"
                                                class="form-check"
                                                >
                                                <input
                                                    class="form-check-input"
                                                    type="radio"
                                                    :name="dq.model"
                                                    :id="`${dq.model}-${opt_index}`"
                                                    v-model="safeForms[fIdx][dq.model]"
                                                    :value="option.value || option.text"
                                                    :required="dq.required && dq.is_visible"
                                                    :disabled="!hasWriteAccess(section)"
                                                />
                                                <label class="form-check-label text-xxs">
                                                    {{ option.text }}
                                                </label>
                                                </div>
                                            </div>

                                            <!-- Checkbox -->
                                            <div
                                                v-else-if="dq.type === 'multi_select' || dq.type === 'checkboxes'"
                                                class="mt-2"
                                            >
                                                <div
                                                v-for="(option, opt_index) in dq.options || []"
                                                :key="'dq-checkbox-' + opt_index"
                                                class="form-check"
                                                >
                                                <input
                                                    class="form-check-input"
                                                    type="checkbox"
                                                    :id="`${dq.model}-${opt_index}`"
                                                    :value="option.value || option.text"
                                                    v-model="safeForms[fIdx][dq.model]"
                                                    :disabled="!hasWriteAccess(section)"
                                                />
                                                <label class="form-check-label text-xxs">
                                                    {{ option.text }}
                                                </label>
                                                </div>
                                            </div>

                                            <!-- File / facial / fallback remain unchanged -->
                                            <slot />
                                            </div>
                                        </div>
                                        </div>
                                    </template>
                                </div>

                                <!-- Verification / Actions - Only show for Write access and System Admin -->
                                <div v-if="!is_entity_user && hasWriteAccess(section)"
                                    class="d-flex justify-content-end align-items-center mt-3 p-2">
                                    <div class="d-flex gap-2 mt-2">
                                        <button v-if="!section.is_verified"
                                            class="btn btn-sm text-xxs shadow btn-primary-dark"
                                            :class="{ 'opacity-50': !is_verify_allowed }" :disabled="!is_verify_allowed"
                                            @click="is_verify_allowed ? section_verification(section.id) : null">
                                            Verify
                                        </button>
                                        <button class="btn btn-sm text-xxs shadow btn-primary-dark"
                                            :class="{ 'opacity-50': !is_request_information_allowed }"
                                            :disabled="!is_request_information_allowed" data-bs-toggle="modal"
                                            data-bs-target="#request_information_modal"
                                            @click="is_request_information_allowed ? prepare_request_information(fIdx, sIdx) : null">
                                            Request Information
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </template>

                <div v-else class="text-muted small">No sections in this tab.</div>
                <div v-if="hasAnyWriteAccess(form)" class="d-flex justify-content-center align-items-center mb-2">
                    <button v-if="fIdx !== safeFormConfig.length - 1"
                        class="btn btn-sm rounded-small text-xxs shadow text-dark"
                        :class="can_submit ? 'btn-primary-dark' : 'disabled'" :disabled="!can_submit"
                        @click="can_submit ? next_form() : null">
                        Save
                    </button>
                    <template v-else>
                        <button class="btn btn-sm rounded-small text-xxs shadow text-dark"
                            :class="can_submit ? 'btn-primary-dark' : 'disabled'" :disabled="!can_submit"
                            @click="can_submit ? submitAnswers(false) : null">
                            Save
                        </button>
                        <button class="btn btn-sm rounded-small text-xxs shadow text-dark ms-2"
                            :class="can_submit ? 'btn-primary-dark' : 'disabled'" :disabled="!can_submit"
                            @click="can_submit ? submitAnswers(true) : null">
                            Submit
                        </button>
                    </template>
                </div>
            </div>
        </div>
    </div>

    <div v-else-if="isLoading" class="text-center py-3">
        <div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>
    </div>
    <div v-else class="text-muted text-center">No form tabs found.</div>

    <!-- File preview modal shell -->
    <teleport to="body">
        <div v-if="showFilePreviewModal" @click.self="closeFilePreview">
            <div class="custom-modal-content">
                <div class="custom-modal-header">
                    <h5>File Preview</h5>
                    <button type="button" class="custom-modal-close" @click="closeFilePreview">&times;</button>
                </div>
            </div>
        </div>
    </teleport>

<!-- File comments modal -->
<teleport to="body">
    <div class="modal fade" id="comment_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg">
            <div class="modal-content shadow-lg d-flex flex-column">
                <div class="modal-header cute-header">
                    <p class="modal-title text-white text-xxs">File Comments for {{ user_full_name }}</p>
                    <button type="button" class="modal-btn-close btn-close" data-bs-dismiss="modal"
                        aria-label="Close"></button>
                </div>

                <div class="modal-body d-flex flex-column" v-if="current_comments">

                    <!-- Comments Section - Scrollable ONLY here -->
                    <div v-if="current_comments.length" class="max-h mb-3 p-3 rounded-lg shadow">
                        <div
                            v-for="(msg, i) in current_comments"
                            :key="i"
                            class="mb-3 d-flex"
                            :class="msg.user_full_name !== entity_name ? 'justify-content-start' : 'justify-content-end'"
                        >
                            <div class="d-flex flex-column"
                                :class="msg.user_full_name !== entity_name ? 'align-items-start' : 'align-items-end'">
                                <small class="mb-1 fw-bold"
                                    :class="msg.user_full_name !== entity_name ? 'text-start' : 'text-end'">
                                    {{ msg.user_full_name }}
                                </small>
                                <div class="p-2 rounded-pill shadow text-xxs bg-primary-blue">
                                    <div>{{ msg.comment }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else class="text-xxs text-muted mb-3">No previous comments.</div>

                    <!-- Textarea Section - stays fixed below -->
                    <textarea
                        v-model="comment_note"
                        class="comment-textarea form-control text-xxs flex-shrink-0 rounded-small"
                        rows="3"
                        placeholder="Add a note..."
                    ></textarea>
                </div>

                <div class="modal-footer border-top">
                    <button type="button" class="btn btn-primary-dark btn-sm text-xxs" @click="comment_submit">
                        Add Comment
                    </button>
                </div>
            </div>
        </div>
    </div>
</teleport>
<div v-if="preview_modal" @click.self="preview_modal = false">
    <FilePreviewComponent :presigned_url="selected_url" @close="close_the_file_preview" :key="selected_url" />
</div>
</template>
